import React, { type ReactNode } from "react";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import MuiCard from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActionArea from "@material-ui/core/CardActionArea";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";

import type { Theme } from "src/theme";

export interface CardProps {
  title: ReactNode;
  content: ReactNode;
  onClick?: () => void;
  highlighted?: boolean;
}

const useStyles = makeStyles((theme: Theme) => {
  const { custom: palette, derived } = theme.palette.custom;

  // Convenience variables for gradient
  const brandPrimary = palette.brandPrimary;
  const brandSecondary = palette.brandSecondary;
  const brandPrimaryLight = derived.brandPrimaryLight;
  const brandSecondaryLight = derived.brandSecondaryLight;

  return {
    "@keyframes gradientFlow": {
      "0%": {
        backgroundPosition: "0% 50%",
      },
      "50%": {
        backgroundPosition: "100% 50%",
      },
      "100%": {
        backgroundPosition: "0% 50%",
      },
    },
    wrapper: {
      position: "relative",
      height: "100%",
    },
    wrapperClickable: {
      cursor: "pointer",
      transition: "transform 0.2s ease",
      "&:hover, &$wrapperHighlighted": {
        transform: "translateY(-4px)",
        "& $glow": {
          opacity: 1,
        },
      },
    },
    wrapperHighlighted: {},
    glow: {
      position: "absolute",
      top: -3,
      left: -3,
      right: -3,
      bottom: -3,
      background: `linear-gradient(90deg, ${brandSecondary}, ${brandPrimary}, ${brandSecondaryLight}, ${brandPrimaryLight}, ${brandSecondary}, ${brandPrimary}, ${brandSecondaryLight}, ${brandPrimaryLight}, ${brandSecondary})`,
      backgroundSize: "300% 100%",
      borderRadius: theme.shape.borderRadius + 3,
      opacity: 0,
      filter: "blur(10px)",
      transition: "opacity 0.3s ease",
      animation: "$gradientFlow 4s ease infinite",
    },
    card: {
      position: "relative",
      height: "100%",
    },
    actionArea: {
      height: "100%",
    },
    title: {
      color: derived.titleOnCard,
      fontWeight: theme.typography.fontWeightMedium,
    },
    content: {
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.h5.fontSize,
    },
  };
});

function Card({ title, content, onClick, highlighted }: CardProps) {
  const classes = useStyles();

  const cardContent = (
    <CardContent>
      <Typography
        variant="subtitle1"
        component="h3"
        gutterBottom
        className={classes.title}
      >
        {title}
      </Typography>
      <Typography
        variant="h6"
        component="p"
        align="center"
        className={classes.content}
      >
        {content}
      </Typography>
    </CardContent>
  );

  const card = (
    <MuiCard className={classes.card}>
      {onClick ? (
        <CardActionArea onClick={onClick} className={classes.actionArea}>
          {cardContent}
        </CardActionArea>
      ) : (
        cardContent
      )}
    </MuiCard>
  );

  if (onClick) {
    return (
      <Box
        className={clsx(
          classes.wrapper,
          classes.wrapperClickable,
          highlighted && classes.wrapperHighlighted
        )}
      >
        <Box className={classes.glow} />
        {card}
      </Box>
    );
  }

  return <Box className={classes.wrapper}>{card}</Box>;
}

export default Card;
